import React, { useContext, useMemo } from "react"
import CheckboxWithLabelForForm from "../../../../../ui/CheckboxWithLabelForForm"
import Button from "../../../../../ui/Button"
import { pricingMatricElectricLabelMap, pricingMatrixHeaderMap } from "../../constants"
import { PriceConfig } from "../../types"
import { SiteProps } from "~/interfaces"
import { ELECTRICS } from "~/constants"
import { validDurations, validElectric } from "~/types"
import { AdminContext } from "~/contexts/AdminContext"
import { formatMoney } from "~/utils/formatMoney"

interface SettingsPricingTableProps {
  priceConfig: PriceConfig
  onNewPrice: () => void
  onEditPrice: (priceKey: string, amount_in_cents: number, duration: validDurations, electric: validElectric) => void
  sites: SiteProps[]
  headers: Array<{
    duration: validDurations
    electrics: Array<{
      electric: validElectric
      amounts: number[]
    }>
  }>
  enablePrice: (key: string, siteId: string) => void
  disablePrice: (key: string, siteId: string) => void
  selectAll: (key: string) => void
  unselectAll: (key: string) => void
  sitePriceCounts: { [siteId: string]: number }
}

const SettingsPricingTable = ({
  priceConfig,
  onNewPrice,
  onEditPrice,
  sites,
  headers,
  enablePrice,
  disablePrice,
  selectAll,
  unselectAll,
  sitePriceCounts,
}: SettingsPricingTableProps) => {
  const { camp } = useContext(AdminContext)
  if (!sites || !camp) return null

  const sortedSites = useMemo(() => {
    return [...sites].sort((a, b) => {
      const extractNumber = (name: string) => {
        const match = name.match(/\d+/)
        return match ? parseInt(match[0], 10) : 0
      }

      const aNum = extractNumber(a.name)
      const bNum = extractNumber(b.name)

      return aNum - bNum
    })
  }, [sites])

  return (
    <div className="container mx-auto px-4 py-4 overflow-auto">
      <h2 className="text-xl mb-2 w-full text-center font-bold">Prices</h2>
      <table className="min-w-full table-auto border-collapse">
        <thead>
          <tr>
            <th className="text-center">
              <Button variant="link" onClick={onNewPrice}>
                Add new price +
              </Button>
            </th>
            {headers.map(({ duration, electrics }) => (
              <th
                key={duration}
                className="border border-gray-300 text-center"
                colSpan={electrics.reduce((sum, electric) => sum + electric.amounts.length, 0)}
              >
                {pricingMatrixHeaderMap(duration)}
              </th>
            ))}
            <th></th>
          </tr>
          <tr>
            <th></th>
            {headers.map(({ electrics }) =>
              electrics.map(({ electric, amounts }) => (
                <th
                  key={`${electric}-header`}
                  className="border border-gray-300 text-center font-semibold"
                  colSpan={amounts.length}
                >
                  {electric !== ELECTRICS.NONE ? pricingMatricElectricLabelMap(electric) : "No Electric"}
                </th>
              )),
            )}
          </tr>
          <tr>
            <th></th>
            {headers.map(({ duration, electrics }) =>
              electrics.map(({ electric, amounts }) =>
                amounts.map((amount) => {
                  const key = `${duration}_${electric}_${amount}`
                  return (
                    <th
                      key={key}
                      className="border border-gray-300 text-center font-semibold hover:underline hover:cursor-pointer"
                      onClick={() => onEditPrice(key, amount, duration, electric)}
                    >
                      {amount !== undefined ? formatMoney(amount / 100, camp.currency) : ""}
                    </th>
                  )
                }),
              ),
            )}
          </tr>
          <tr>
            <th className="px-4 py-2 border border-gray-300 bg-gray-100 text-left">Select All</th>
            {headers.flatMap(({ duration, electrics }) =>
              electrics.flatMap(({ electric, amounts }) =>
                amounts.map((amount) => {
                  const key = `${duration}_${electric || "no_electric"}_${amount}`
                  return (
                    <th key={key} className="px-4 py-2 border border-gray-300 bg-gray-100 text-center">
                      <CheckboxWithLabelForForm
                        className="justify-center my-2"
                        label=""
                        value={sites.every((site) =>
                          priceConfig[key]?.some((price) => price.siteId === site.id && !price._destroy),
                        )}
                        setValue={(checked) => {
                          if (checked) {
                            selectAll(key)
                          } else {
                            unselectAll(key)
                          }
                        }}
                      />
                    </th>
                  )
                }),
              ),
            )}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {sortedSites.map((site, siteIdx) => {
            const siteId = site.id
            return (
              <tr key={siteIdx}>
                <td className="py-2 border border-gray-300">{site.name}</td>
                {headers.map(({ duration, electrics }) =>
                  electrics.flatMap(({ electric, amounts }) => {
                    const isAnyChecked = amounts.some((amount) => {
                      const key = `${duration}_${electric || "no_electric"}_${amount}`
                      const priceEntry = priceConfig[key]?.find((price) => price.siteId === siteId)
                      return priceEntry ? !priceEntry._destroy : false
                    })
                    return amounts.map((amount) => {
                      const key = `${duration}_${electric || "no_electric"}_${amount}`

                      const priceEntry = priceConfig[key]?.find((price) => price.siteId === siteId)

                      const isChecked = priceEntry ? !priceEntry._destroy : false

                      // Disable if:
                      // 1. The site has only one price and this checkbox is checked.
                      // 2. Another amount in the same group is checked and this checkbox is not the one that's checked.
                      const isDisabled = (sitePriceCounts[siteId] === 1 && isChecked) || (isAnyChecked && !isChecked)

                      return (
                        <td key={`${key}_${site.id}`} className="px-4 py-2 border border-gray-300 text-center">
                          <CheckboxWithLabelForForm
                            name={key}
                            className="justify-center my-2"
                            label=""
                            disabled={isDisabled}
                            value={isChecked}
                            setValue={(checked) => {
                              if (checked) {
                                enablePrice(key, siteId)
                              } else {
                                disablePrice(key, siteId)
                              }
                            }}
                          />
                        </td>
                      )
                    })
                  }),
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

export default SettingsPricingTable
